import { useEffect, useState } from "react";
import StackedAreaChart, { type DashboardUsageData } from "./StackedAreaChart";
import RadialBarChart from "./RadialBarChart";
import { useSubscriptionUsageTimeseries } from "../hooks/useApi";
import { formatNumber, toPersianDigits } from "../utils/format";

interface FixedUsageDrawerProps {
  open: boolean;
  onClose: () => void;
  fixedSub?: Record<string, unknown> | null;
  fixedUsage?: Record<string, unknown> | null;
}

function daysLeft(endDate?: string | null): number | null {
  if (!endDate) return null;
  return Math.max(
    0,
    Math.ceil((new Date(endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
  );
}

function StatBox({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="rounded-btn bg-surface-base p-4">
      <p className="text-small text-text-tertiary mb-1">{label}</p>
      <p className="text-h2 font-semibold font-number text-text-main">
        {value}
        {unit ? <span className="text-small text-text-tertiary font-normal mr-1">{unit}</span> : null}
      </p>
    </div>
  );
}

export default function FixedUsageDrawer({
  open,
  onClose,
  fixedSub,
  fixedUsage,
}: FixedUsageDrawerProps) {
  const [metric, setMetric] = useState<"tokens" | "requests">("tokens");
  const tokenTimeseries = useSubscriptionUsageTimeseries(open, "tokens");
  const requestTimeseries = useSubscriptionUsageTimeseries(open, "requests");
  const selectedTimeseries = metric === "tokens" ? tokenTimeseries : requestTimeseries;

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  if (!open) return null;

  const rawPlanName = (fixedSub?.plan_name as string) || "تعرفه ثابت";
  const planName = toPersianDigits(rawPlanName);
  const left = daysLeft(fixedSub?.end_date as string | undefined);

  const limit = Number((fixedUsage as { monthly_token_limit?: unknown } | undefined)?.monthly_token_limit) || 0;
  const used = Number((fixedUsage as { used_tokens?: unknown } | undefined)?.used_tokens) || 0;
  const remaining = Number((fixedUsage as { remaining_tokens?: unknown } | undefined)?.remaining_tokens) || 0;
  const requests = Number((fixedUsage as { requests_count?: unknown } | undefined)?.requests_count) || 0;
  const pct = Math.max(
    0,
    Math.min(
      100,
      Number((fixedUsage as { used_percent?: unknown } | undefined)?.used_percent) ||
        (limit ? (used / limit * 100) : 0)
    )
  );

  const chartData = selectedTimeseries.data as DashboardUsageData | undefined;
  const chartLoading = selectedTimeseries.isLoading && !chartData;

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Drawer panel — slides in from the left, fills half the page width */}
      <aside className="absolute top-0 bottom-0 left-0 w-full md:w-1/2 bg-surface-base border-r border-line shadow-[0_20px_60px_rgba(0,0,0,0.15)] flex flex-col overflow-hidden animate-drawer-left">
        {/* Header */}
        <div className="bg-surface px-6 pt-5 pb-4 border-b border-line">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-small text-text-tertiary mb-1">جزئیات مصرف تعرفه ثابت</p>
              <h2 className="text-h3 font-semibold text-text-main">{planName}</h2>
            </div>
            <button
              onClick={onClose}
              className="text-text-tertiary hover:text-text-main transition-colors p-1 shrink-0"
              aria-label="بستن"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Stat boxes — last active plan duration only */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <StatBox label="درخواست‌ها" value={formatNumber(requests)} />
            <StatBox label="حجم توکن" value={formatNumber(used)} />
            <StatBox label="توکن باقی مانده" value={formatNumber(remaining)} />
            <StatBox label="روزهای باقی مانده" value={left !== null ? toPersianDigits(String(left)) : "—"} />
          </div>

          {/* Monthly usage bar */}
          {limit > 0 && (
            <div className="mt-4">
              <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "rgb(var(--chart-blue) / 0.15)" }}>
                <div className="h-full" style={{ width: `${Math.round(pct)}%`, background: pct >= 90 ? "#dc2626" : "rgb(var(--chart-blue))" }} />
              </div>
              <p className="text-small text-text-tertiary mt-1.5">
                {formatNumber(used)} از {formatNumber(limit)} توکن مصرف شده در دوره این پلن
              </p>
            </div>
          )}

          {/* Usage summary — same metric controls as the PAYG dashboard */}
          <div className="mt-6">
            <h3 className="text-h3 text-text-main font-bold mb-3">خلاصه مصرف</h3>
            <p className="text-body text-text-secondary mb-5">
              {chartData
                ? `در این دوره، مجموعاً ${formatNumber(chartData.summary?.tokens || 0)} توکن در ${formatNumber(chartData.summary?.requests || 0)} درخواست مصرف شده است.`
                : "در حال بارگذاری…"}
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <h4 className="text-h3 text-text-main font-bold">
                    مصرف به تفکیک مدل — {metric === "tokens" ? "توکن‌ها" : "درخواست‌ها"}
                  </h4>
                  <div className="flex items-center gap-1 p-1 rounded-btn bg-surface-hover" role="tablist" aria-label="معیار مصرف">
                    {([['tokens', 'توکن‌ها'], ['requests', 'درخواست‌ها']] as const).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        role="tab"
                        aria-selected={metric === value}
                        onClick={() => setMetric(value)}
                        className={`px-3 py-1.5 rounded-btn text-small transition-colors ${
                          metric === value
                            ? "bg-surface text-primary font-semibold shadow-sm"
                            : "text-text-secondary hover:text-text-main"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <StackedAreaChart
                  data={chartData}
                  duration="30d"
                  metric={metric}
                  loading={chartLoading}
                  showFullDateLabels
                />
              </div>
              <div>
                <h4 className="text-h3 text-text-main font-bold mb-3">مدل‌های برتر</h4>
                <RadialBarChart data={chartData} metric={metric} duration="30d" loading={chartLoading} />
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
